import React, { useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import axios from "axios";
import Modal from "react-modal";
import Loader from "../Loaders/Loader";
import { IoTrashSharp, IoPencil } from "react-icons/io5";
import "./styles/UserProfile.css";
import avatarPlaceholder from "../assets/User.png";
import ConfirmationDialog from "../utils/ConfirmationDialog";
import HistoryModal from "../utils/HistoryModal";
import { FaHistory } from "react-icons/fa";
import ManageUserSessionModal from "../utils/ManageUserSessionModal";
import { TbDeviceDesktopCode } from "react-icons/tb";

Modal.setAppElement("#root");

interface UserProfileData {
  user_uid: string;
  org_uid: string;
  privilege: string;
  user_role: string;
  first_name: string;
  last_name: string;
  user_status: string;
  theme: string;
  email: string;
  timezone: string;
  session_timeout: string;
  refresh_time: string;
  metric_filter_type: string;
  metric_filter_value: string;
  user_manage_access: boolean;
  user_manage_role: string;
  last_active: string;
  date_of_birth: string;
  release_group: string;
  is_log_enabled: boolean;
  is_force_data_push_enabled: boolean;
  is_leader: boolean;
  image_uid?: string;
}

interface PublicUser {
  user_uid: string;
  first_name: string;
  last_name: string;
  privilege: string;
  user_status: string;
  image_details?: {
    image_uid: string;
    default: boolean;
  };
  user_online_status?: string;
}

const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source");

  const sessionUid = localStorage.getItem("session_uid") ?? "";
  const userUid = localStorage.getItem("user_uid") ?? "";
  const API_URL_USER_PROFILE =
    source === "manageTeams"
      ? `${process.env.REACT_APP_API_URL}/api/team-member-profile`
      : `${process.env.REACT_APP_API_URL}/api/user-profile`;

  const [user, setUser] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyUid, setHistoryUid] = useState<string | null>(null);

  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [userSessions, setUserSessions] = useState<any[]>([]);
  const [userImage, setUserImage] = useState<string | null>(null);

  const [publicUser, setPublicUser] = useState<PublicUser | null>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!userId) {
        setError("User ID is missing.");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(API_URL_USER_PROFILE, {
          headers: {
            Accept: "application/json",
            "user-uid": userUid,
            "target-user-uid": userId,
          }, withCredentials: true,
        });

        if (response.status !== 200) {
          throw new Error("Failed to fetch user details");
        }

        const userData = response.data.data;
        setUser(userData);
        publicfetchUser(userId);
        if (userData.image_uid && userData.image_uid.trim() !== "") {
          try {
            const url = new URL(
              `${process.env.REACT_APP_API_URL}/api/get-image-by-uid`
            );
            url.searchParams.append("image_uid_list", userData.image_uid);
            url.searchParams.set("is_icon_img", "true");
            url.searchParams.set("is_feedback_image", "false");

            const res = await fetch(url.toString(), {
              headers: {
                "user-uid": userUid,
              }, credentials: "include",
            });

            const imgData = await res.json();

            if (
              imgData.status === "Success" &&
              Array.isArray(imgData.data) &&
              imgData.data.length > 0
            ) {
              const imageBase64 = imgData.data[0].image;
              setUserImage(imageBase64);
            } else {
              setUserImage(null);
            }
          } catch (error) {
            console.error("Failed to fetch user image:", error);
            setUserImage(null);
          }
        } else {
          setUserImage(null);
        }

        if (userData.history_uid) {
          setHistoryUid(userData.history_uid);
        }
        localStorage.setItem(
          "two_fa_enabled",
          userData.two_fa_enabled.toString()
        );
        localStorage.setItem("email1", userData.email);
      } catch (err) {
        setError("Error fetching user details");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [userId]);

  const publicfetchUser = async (targetUid: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/public-user-profile`,
        {
          headers: {
            "Content-Type": "application/json",
            "user-uid": userUid,
          }, withCredentials: true,
          params: {
            target_user_uid: targetUid,
          },
        }
      );

      if (response.data.status === "Success") {
        setPublicUser(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching public user profile:", error);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "active":
        return { color: "#16821aff", label: "Online", iconname: "checkcircle" }; // green
      case "away":
        return { color: "#f6c70aff", label: "Away", iconname: "clockcircle" }; // yellow
      case "invited":
        return {
          color: "#000000",
          label: "Inactive",
          iconname: "exclamationcircle",
        };
      default:
        return {
          color: "#9b9292ff",
          label: "Offline",
          iconname: "closecircle",
        };
    }
  };

  const handleDelete = async () => {
    if (!user) return;

    try {
      await axios.delete(API_URL_USER_PROFILE, {
        headers: {
          "user-uid": userUid,
          "Content-Type": "application/json",
        }, withCredentials: true,
        data: {
          user_uid: user.user_uid,
          org_uid: user.org_uid,
        },
      });

      alert("User deleted successfully.");
      navigate(-1);
    } catch (err) {
      console.error("Failed to delete user:", err);
      alert("Failed to delete user.");
    } finally {
      setIsModalOpen(false);
    }
  };

  if (loading) return <Loader />;
  if (error) return <div className="error-message">{error}</div>;
  if (!user) return <div className="error-message">User not found.</div>;

  return (
    <div className="UserProfileWholeContainer">
      <h2 className="UserProfilePageHeading">User Details</h2>
      <div className="user-profile-container">
        <div className="user-header">
          {/* <div className="user-image">
            <img
  src={
    userImage
      ? `data:image/png;base64,${userImage}`
      : avatarPlaceholder
  }
  alt={user.first_name + " " + user.last_name}
  className="userProfile-avatar"
/>

          </div> */}
          {/* <div className="user-image">
            <img
              src={
                userImage
                  ? `data:image/png;base64,${userImage}`
                  : avatarPlaceholder
              }
              alt={user.first_name + " " + user.last_name}
              className="userProfile-avatar"
            />

            <span
              className="user-status-dot"
              style={{
                backgroundColor: getStatusInfo(
                  publicUser?.user_online_status || "offline"
                ).color,
              }}
            ></span>
          </div> */}
          <div className="user-image" style={{ textAlign: "center" }}>
            <img
              src={
                userImage
                  ? `data:image/png;base64,${userImage}`
                  : avatarPlaceholder
              }
              alt={user.first_name + " " + user.last_name}
              className="userProfile-avatar"
            />

            <span
              className="user-status-dot"
              style={{
                backgroundColor: getStatusInfo(
                  publicUser?.user_online_status || "offline"
                ).color,
              }}
            ></span>

            {/* 👇 Online Status Text */}
            <div
              style={{
                marginTop: "6px",
                fontSize: "0.9rem",
                fontWeight: 500,
                color: getStatusInfo(
                  publicUser?.user_online_status || "offline"
                ).color,
                textTransform: "capitalize",
              }}
            >
              {getStatusInfo(publicUser?.user_online_status || "offline").label}
            </div>
          </div>

          <div className="user-profile-name">
            <strong style={{ color: "var(--text-color)" }}>
              {user.first_name} {user.last_name}
            </strong>
            <span style={{ color: "#6B7280" }}>{user.email}</span>
          </div>
          <div
            className="user-profile-buttons"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
              marginTop: "10px",
            }}
          >
            <div
              className="left-buttons"
              style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
            >
              <button
                className="userProfile-edit-btn"
                style={{
                  flex: 1,
                  minWidth: "150px",
                  padding: "10px 16px",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  // backgroundColor: "#176164",
                  color: "#fff",
                  fontWeight: 500,
                }}
                onClick={() => navigate("/manage-user-edit", { state: user })}
              >
                <IoPencil style={{ fontSize: "0.9rem", marginRight: "10px" }} />
                Edit User
              </button>

              <button
                style={{
                  flex: 1,
                  minWidth: "150px",
                  padding: "10px 16px",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  backgroundColor: "var(--sidebar-hover-color)",
                  color: "#fff",
                  fontWeight: 500,
                }}
                onClick={() => setShowSessionsModal(true)}
              >
                <TbDeviceDesktopCode
                  style={{ fontSize: "0.9rem", marginRight: "10px" }}
                />
                View Sessions
              </button>
            </div>

            <div
              className="right-buttons"
              style={{
                display: "flex",
                flexDirection: "column", // ✅ stack vertically
                gap: "10px",
                flexWrap: "wrap",
                alignItems: "flex-start", // ✅ align buttons and link neatly
              }}
            >
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <button
                  className="userProfile-viewhistory-btn"
                  style={{
                    flex: 1,
                    minWidth: "150px",
                    padding: "10px 16px",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    // backgroundColor: "#176164",
                    color: "#fff",
                    fontWeight: 500,
                  }}
                  onClick={() => setShowHistoryModal(true)}
                  disabled={!historyUid}
                >
                  <FaHistory
                    style={{ fontSize: "0.9rem", marginRight: "10px" }}
                  />
                  View History
                </button>
              </div>

              {/* ✅ Move the link here, right below Delete User */}
              <Link
                to="/forcedresetpassword"
                className="userProfile-reset-password-link"
                style={{
                  marginTop: "5px",
                  fontSize: "0.9rem",
                  textDecoration: "underline",
                  color: "#007bff",
                  cursor: "pointer",
                }}
                state={{
                  user_uid: user.user_uid,
                  sessionUid: sessionUid,
                  userUid: userUid,
                  org_uid: user.org_uid,
                  site_uid: (user as any).site_uid,
                  privilege: user.privilege,
                  user_role: user.user_role,
                  device_control: (user as any).device_control,
                }}
              >
                Forced Reset Password ?
              </Link>
            </div>
          </div>
        </div>

        <div className="user-info">
          <div className="user-details">
            <div className="user-detail-row">
              <span className="userProfile-label">User ID:</span>
              <span className="userProfile-value">{user.user_uid}</span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Org ID:</span>
              <span className="userProfile-value">{user.org_uid}</span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Role:</span>
              <span className="userProfile-value">{user.user_role}</span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Privilege:</span>
              <span className="userProfile-value">{user.privilege}</span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Status:</span>
              <span className="userProfile-value">{user.user_status}</span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Date of Birth:</span>
              <span className="userProfile-value">
                {user.date_of_birth && user.date_of_birth.trim() !== ""
                  ? user.date_of_birth
                  : "NA"}
              </span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Manage Access:</span>
              <span className="userProfile-value">
                {user.user_manage_access ? "Yes" : "No"}
              </span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Last Active:</span>
              <span className="userProfile-value">
                {user.last_active
                  ? new Date(user.last_active).toLocaleString()
                  : "NA"}
              </span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Session Timout:</span>
              <span className="userProfile-value">
                {user.session_timeout ? user.session_timeout.split(":")[1] : ""}{" "}
                minutes
              </span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Refresh Timeout:</span>
              <span className="userProfile-value">
                {user.refresh_time
                  ? user.refresh_time.includes(":")
                    ? user.refresh_time.split(":")[1] + " minutes"
                    : user.refresh_time + " minutes"
                  : "NA"}
              </span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Release Group:</span>
              <span className="userProfile-value">{user.release_group}</span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Log Enabled:</span>
              <span className="userProfile-value">
                {user.is_log_enabled ? "Yes" : "No"}
              </span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Data Push Enabled:</span>
              <span className="userProfile-value">
                {user.is_force_data_push_enabled ? "Yes" : "No"}
              </span>
            </div>
            <div className="user-detail-row">
              <span className="userProfile-label">Leader:</span>
              <span className="userProfile-value">
                {user.is_leader ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </div>
        <div className="userProfile-actions">
          <button className="userProfile-back-btn" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </div>

      <HistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        params={{ history_uid: historyUid! }}
      />
      <ConfirmationDialog
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDelete}
        onCancel={() => setIsModalOpen(false)}
        message={`Are you sure you want to delete ${user.first_name} ${user.last_name}?`}
        confirmText="Delete"
        cancelText="Cancel"
        close={false}
      />
      {showSessionsModal && (
        <ManageUserSessionModal
          targetUser={user.email}
          onClose={() => setShowSessionsModal(false)}
          userUid={userUid}
          sessionUid={sessionUid}
          org_uid={user.org_uid}
        />
      )}
    </div>
  );
};

export default UserProfile;
